import { useEffect, useRef } from 'react';
import { assets } from '@/assets';
import {
  CYCLE_S,
  POSES,
  blendPoses,
  poseWeight,
  tongueOutline,
  type PoseId,
} from '@/content/articulation';
import { cx } from '@/lib/cx';
import styles from './MouthCutaway.module.css';

/**
 * The animated side view of the mouth (issue #25): the coach diagram with its tongue removed
 * (ml/art/tongueless_coach.py) and a tongue drawn from the reviewed poses (content/articulation.ts)
 * that moves from rest into the pose and back. The lower front teeth are a layer in front, so the
 * tongue moves behind them. Still in the pose for reduced motion; paused off screen.
 *
 * Registration: mm (z forward, y up, origin at the upper incisors' edge) to the diagram's SVG
 * (3.824 px/mm, rotated 10.7 degrees, ml/experiments/sagittal_overlay.py), then to the coach art's
 * 1254 px frame (x 3.04 + 40, ml/art/normalize_coach_art.py).
 */
const K = 3.04 * 3.824;
const ROT = (10.7 * Math.PI) / 180;
const MM_TO_ART = [
  -K * Math.cos(ROT),
  -K * Math.sin(ROT),
  K * Math.sin(ROT),
  -K * Math.cos(ROT),
  3.04 * 97.1 + 40,
  3.04 * 174.5,
]
  .map((v) => v.toFixed(4))
  .join(' ');
/** The art's full frame, and the part shown (square): the mouth and throat. */
const ART = 1254;
const VIEW = '190 240 940 940';

function tonguePath(pose: PoseId, weight: number): string {
  const outline = tongueOutline(blendPoses(POSES.rest, POSES[pose], weight).profile);
  return `${outline.map((p, i) => `${i ? 'L' : 'M'}${p.z.toFixed(2)} ${p.y.toFixed(2)}`).join('')}Z`;
}

interface MouthCutawayProps {
  pose: PoseId;
  /** Accessible description of what the view shows. */
  label: string;
  /** Hold the pose instead of moving into it and back (also with reduced motion). */
  still?: boolean;
  /** Times to move into the pose and back before holding it (short, not repetitive). */
  cycles?: number;
  className?: string;
}

export function MouthCutaway({
  pose,
  label,
  still = false,
  cycles = 2,
  className,
}: MouthCutawayProps) {
  const host = useRef<SVGSVGElement>(null);
  const tongue = useRef<SVGPathElement>(null);

  // Plays again from rest whenever the pose changes.
  useEffect(() => {
    const el = host.current;
    const path = tongue.current;
    if (!el || !path) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    let start: number | undefined;
    let raf = 0;
    let visible = false;
    const frame = (now: number) => {
      start ??= now;
      const seconds = (now - start) / 1000;
      const done = still || reduced.matches || seconds >= cycles * CYCLE_S;
      path.setAttribute('d', tonguePath(pose, done ? 1 : poseWeight(seconds)));
      if (visible && !done) raf = requestAnimationFrame(frame);
    };
    const observer = new IntersectionObserver(([entry]) => {
      const now = entry?.isIntersecting ?? false;
      if (now && !visible) raf = requestAnimationFrame(frame);
      visible = now;
    });
    observer.observe(el);
    return () => {
      visible = false;
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [pose, still, cycles]);

  return (
    <svg
      ref={host}
      className={cx(styles.root, className)}
      viewBox={VIEW}
      role="img"
      aria-label={label}
    >
      <image href={assets.coach.mouth.url} width={ART} height={ART} />
      <g transform={`matrix(${MM_TO_ART})`}>
        <path ref={tongue} className={styles.tongue} d={tonguePath(pose, 1)} />
      </g>
      <image href={assets.coach.mouthTeeth.url} width={ART} height={ART} />
    </svg>
  );
}
